import classNames from "classnames";
import React, { CSSProperties, useMemo, useState } from "react";
import {
  isWebsiteBuilderElementWithBuilder,
  isWebsiteBuilderStretchElementData,
  TWebsiteBuilderElement,
  websiteBuilderElementFilled,
  websiteBuilderGenerateElementDesignStyles,
} from "../../../../nodes/element/websiteBuilderElement";
import { useWebsiteBuilderEditorContext } from "../../../../websiteBuilderEditorContext";
import { useActivePageDomNodeRefMapRegistration } from "../../../../hooks/activePageDomNodeRefMap/useActivePageDomNodeRefMapRegistration";
import { useAppDispatch, useAppSelector } from "hooks/redux";
import {
  websiteBuilderSelectNode,
  websiteBuilderUpdateSelectedNode,
} from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import {
  TUseSectionEntityViewResizeArgs,
  useSectionEntityViewResize,
} from "../../hooks/sectionEntity/useSectionEntityViewResize/useSectionEntityViewResize";
import { WEBSITE_BUILDER_Z_LAYERS } from "../../../../utility/websiteBuilderZLayers";
import { isWebsiteBuilderButton } from "../../../../nodes/element/websiteBuilderButton";
import { WebsiteBuilderButtonView } from "./button/WebsiteBuilderButtonView";
import { isWebsiteBuilderDivider } from "../../../../nodes/element/websiteBuilderDivider";
import { WebsiteBuilderDividerView } from "./divider/WebsiteBuilderDividerView";
import { isWebsiteBuilderEmbed } from "../../../../nodes/element/websiteBuilderEmbed";
import { WebsiteBuilderEmbedView } from "./embed/WebsiteBuilderEmbedView";
import { isWebsiteBuilderHeading } from "../../../../nodes/element/websiteBuilderHeading";
import { WebsiteBuilderHeadingView } from "./heading/WebsiteBuilderHeadingView";
import { WebsiteBuilderParagraphView } from "./paragraph/WebsiteBuilderParagraphView";
import { isWebsiteBuilderParagraph } from "../../../../nodes/element/websiteBuilderParagraph";
import { isWebsiteBuilderLink } from "../../../../nodes/element/websiteBuilderLink";
import { WebsiteBuilderLinkView } from "./link/WebsiteBuilderLinkView";
import { isWebsiteBuilderList } from "../../../../nodes/element/websiteBuilderList";
import { WebsiteBuilderListView } from "./list/WebsiteBuilderListView";
import { isWebsiteBuilderImage } from "../../../../nodes/element/websiteBuilderImage";
import { WebsiteBuilderImageView } from "./image/WebsiteBuilderImageView";
import { isWebsiteBuilderIcon } from "../../../../nodes/element/websiteBuilderIcon";
import { WebsiteBuilderIconView } from "./icon/WebsiteBuilderIconView";
import { isWebsiteBuilderVideo } from "../../../../nodes/element/websiteBuilderVideo";
import { WebsiteBuilderVideoView } from "./video/WebsiteBuilderVideoView";
import { WebsiteBuilderLogoView } from "./logo/WebsiteBuilderLogoView";
import { isWebsiteBuilderLogo } from "../../../../nodes/element/websiteBuilderLogo";
import { IWebsiteBuilderSectionEntityViewBoundingBox } from "../../hooks/sectionEntity/helpers/sectionEntityViewBoundingBox";
import { useWebsiteBuilderElementMetadata } from "../../../../hooks/elementMetadata/useWebsiteBuilderElementMetadata";
import { isWebsiteBuilderNavigationMenu } from "../../../../nodes/element/websiteBuilderNavigationMenu";
import { WebsiteBuilderNavigationMenuView } from "./navigationMenu/WebsiteBuilderNavigationMenuView";
import { IUseElementViewContextMenuContentArgs, useElementViewContextMenuContent } from "../../hooks/element/useElementViewContextMenuContent/useElementViewContextMenuContent";
import { IUseSectionEntityViewSettingsArgs, useSectionEntityViewSettings } from "../../hooks/sectionEntity/useSectionEntityViewSettings/useSectionEntityViewSettings";
import { IUseSectionEntityViewStretchSettingsArgs, useSectionEntityViewStretchSettings } from "../../hooks/sectionEntity/useSectionEntityViewStretchSettings/useSectionEntityViewStretchSettings";
import { IUseSectionEntityViewContextMenuArgs, useSectionEntityViewContextMenu } from "../../hooks/sectionEntity/useSectionEntityViewContextMenu/useSectionEntityViewContextMenu";
import { IWebsiteBuilderSectionEntityLayoutData, IWebsiteBuilderSectionEntityStretch, websiteBuilderGetSectionEntityActiveLayoutData } from "../../../../nodes/websiteBuilderSectionEntity";
import { IWebsiteBuilderNodeDimensions, IWebsiteBuilderNodePosition } from "../../../../nodes/websiteBuilderNodeGeometry";
import { IUseSectionEntityViewMoveArgs, useSectionEntityViewMove } from "../../hooks/sectionEntity/useSectionEntityViewMove/useSectionEntityViewMove";
import { SectionEntityFrame, TSectionEntityFrameHighlight } from "../sectionEntity/SectionEntityFrame";
import { isWebsiteBuilderSectionEntityInSectionContext, TWebsiteBuilderElementViewContext } from "../../hooks/sectionEntity/helpers/sectionEntityViewContext";
import { isWebsiteBuilderElementContainerParentNode, isWebsiteBuilderElementFlowParentNode, TWebsiteBuilderNode } from "../../../../nodes/websiteBuilderNode";
import { isWebsiteBuilderGrid } from "../../../../nodes/container/websiteBuilderGrid";
import { websiteBuilderGetSectionScreenPosition } from "../../../../nodes/section/websiteBuilderSection";
import { SectionEntityPreviewProvider } from "../sectionEntity/SectionEntityPreviewContext";
import { isWebsiteBuilderTabset, websiteBuilderGetTabsetPosition } from "../../../../nodes/container/websiteBuilderTabset";
import { TWebsiteBuilderContainer } from "../../../../nodes/container/websiteBuilderContainer";

interface IWebsiteBuilderElementViewProps {
  elementContext: TWebsiteBuilderElementViewContext;
}

const ELEMENT_VIEW_MOTION_OPACITY = 0.7;

function WebsiteBuilderElementView(props: IWebsiteBuilderElementViewProps) {
  const { elementContext } = props;

  const dispatch = useAppDispatch();

  const selectedNode = useAppSelector(store => store.websiteBuilderStore.editor.selectedNode);

  const { activePageDomNodeRefMap } = useWebsiteBuilderEditorContext();

  const activePageDomNodeRefMapRegistration = useActivePageDomNodeRefMapRegistration(elementContext.node.id, activePageDomNodeRefMap);

  const elementType = elementContext.node.element.type;

  const designStyles = websiteBuilderGenerateElementDesignStyles(elementContext.parentNode);

  const elementMetadata = useWebsiteBuilderElementMetadata();
  const elementTitle = elementMetadata[elementType].title;

  const elementBuilderUIPortalId = `WEBSITE_BUILDER_ELEMENT_BUILDER_UI_PORTAL_${elementContext.node.id}`;

  const elementInFlowParent = isWebsiteBuilderElementFlowParentNode(elementContext.parentNode);

  const elementSectionNode = elementContext.type === "element-in-section" ? elementContext.parentNode : elementContext.sectionNode;
  
  function getElementContainerNode(): TWebsiteBuilderContainer {
    if (elementContext.type === "element-in-section") {
      return null;
    } else if (elementContext.type === "element-in-container") {
      return elementContext.parentNode;
    } else if (elementContext.type === "element-in-grid-cell") {
      return elementContext.gridNode;
    } else if (elementContext.type === "element-in-tab") {
      return elementContext.tabsetNode;
    } else {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = elementContext;
      return null;
    }
  }

  const elementContainerNode = getElementContainerNode();

  const elementGridCellNode = elementContext.type === "element-in-grid-cell" ? elementContext.parentNode : null;

  const elementTabNode = elementContext.type === "element-in-tab" ? elementContext.parentNode : null;

  const elementDetachedFromTab =
    elementContext.type === "element-in-tab" &&
    selectedNode?.type === "element" &&
    selectedNode.editingMode.type === "element-move" &&
    selectedNode.tabId === elementContext.parentNode.id;

  const {
    elementSelected,
    elementMoving,
    elementResizing,
    elementDetachedFromFlowParent,
    elementSettingsActive,
    elementStretchSettingsActive,
    elementBuilderActive,
    elementContextMenuActive,
  } = useMemo(() => {
    let elementSelected = false;
    let elementMoving = false;
    let elementResizing = false;
    let elementDetachedFromFlowParent = false;
    let elementSettingsActive = false;
    let elementStretchSettingsActive = false;
    let elementBuilderActive = false;
    let elementContextMenuActive = false;
    
    if (selectedNode?.type === "element") {
      elementSelected = elementContext.node.id === selectedNode.id;

      if (elementSelected) {
        const editingModeType = selectedNode.editingMode.type;

        elementMoving = editingModeType === "element-move";
        elementResizing = editingModeType === "element-resize";
        elementDetachedFromFlowParent = (elementMoving || elementResizing) && elementInFlowParent;
        elementSettingsActive = editingModeType === "element-settings";
        elementStretchSettingsActive = editingModeType === "element-stretch-settings";
        elementBuilderActive = editingModeType === "element-builder";
        elementContextMenuActive = editingModeType === "element-context-menu";
      }
    }

    return {
      elementSelected,
      elementMoving,
      elementResizing,
      elementDetachedFromFlowParent,
      elementSettingsActive,
      elementStretchSettingsActive,
      elementBuilderActive,
      elementContextMenuActive,
    };
  }, [
    selectedNode?.type,
    selectedNode?.id,
    selectedNode?.editingMode?.type,
    elementContext.node.id,
    elementInFlowParent,
    elementType,
  ]);

  function getElementGrandParentNode() {
    if (elementContext.type === "element-in-section") {
      return elementContext.rootNode;
    } else if (elementContext.type === "element-in-container") {
      return elementContext.sectionNode;
    } else if (elementContext.type === "element-in-grid-cell") {
      return elementContext.gridNode;
    } else if (elementContext.type === "element-in-tab") {
      return elementContext.tabsetNode;
    } else {
      // eslint-disable-next-line no-unused-vars
      const _exhaustiveCheck: never = elementContext;
      return null;
    }
  }

  const elementGrandParentNode = getElementGrandParentNode();

  const elementFilled =
    websiteBuilderElementFilled(
      elementDetachedFromFlowParent,
      elementContext.node,
      elementContext.parentNode,
      elementGrandParentNode,
    );

  const elementFrameIsChild = !elementDetachedFromFlowParent && elementInFlowParent;

  const [elementHovered, setElementHovered] = useState<boolean>(false);
  
  const elementHighlight: TSectionEntityFrameHighlight = elementHovered && !elementSelected ? "weak" : "none";

  const viewResizeArgs: TUseSectionEntityViewResizeArgs = useMemo(
    () => ({
      context: elementContext,
      selected: elementSelected,
      resizing: elementResizing,
      filled: elementFilled,
    }),
    [
      elementContext,
      elementSelected,
      elementResizing,
      elementFilled,
    ],
  );

  const { viewResizePosition, viewResizeDimensions, viewResizeHandles, viewResizeIndicator } =
    useSectionEntityViewResize(viewResizeArgs);

  const viewMoveArgs: IUseSectionEntityViewMoveArgs = useMemo(
    () => ({
      context: elementContext,
      moving: elementMoving,
      filled: elementFilled,
    }),
    [
      elementContext,
      elementMoving,
      elementFilled,
    ],
  );

  const { viewMovePosition, viewMoveDimensions, viewMoveIndicator, handleViewMovePointerDown } = useSectionEntityViewMove(viewMoveArgs);

  const viewSettingsArgs: IUseSectionEntityViewSettingsArgs = useMemo(
    () => ({
      node: elementContext.node,
      selected: elementSelected,
      settingsActive: elementSettingsActive,
    }),
    [elementContext.node, elementSelected, elementSettingsActive],
  );

  const { viewSettings, handleViewSettingsDoubleClick } = useSectionEntityViewSettings(viewSettingsArgs);

  const viewStretchSettingsArgs: IUseSectionEntityViewStretchSettingsArgs = useMemo(
    () => ({
      node: elementContext.node,
      selected: elementSelected,
      settingsActive: elementStretchSettingsActive,
      parentIsSection: elementContext.parentNode.type === "section",
    }),
    [elementContext.node, elementSelected, elementStretchSettingsActive, elementContext.parentNode.type],
  );

  const { viewStretchSettings, handleViewStretchSettingsClick } = useSectionEntityViewStretchSettings(viewStretchSettingsArgs);

  const viewContextMenuContentArgs: IUseElementViewContextMenuContentArgs = useMemo(() => {
    return {
      node: elementContext.node,
      parentNode: elementContext.parentNode,
      sectionNode: elementSectionNode,
      menuActive: elementContextMenuActive,
      openStretchSettings: handleViewStretchSettingsClick,
    };
  }, [elementContext.node, elementContext.parentNode, elementSectionNode, elementContextMenuActive, handleViewStretchSettingsClick]);

  const { sections: elementContextMenuSections } = useElementViewContextMenuContent(viewContextMenuContentArgs);
  
  const viewContextMenuArgs: IUseSectionEntityViewContextMenuArgs = useMemo(() => {
    return {
      node: elementContext.node,
      selected: elementSelected,
      menuActive: elementContextMenuActive,
      sections: elementContextMenuSections,
    };
  }, [elementContext.node, elementSelected, elementContextMenuActive, elementContextMenuSections]);

  const { viewContextMenu, handleViewOpenContextMenu } = useSectionEntityViewContextMenu(viewContextMenuArgs);

  function getElementBoundingBox(): IWebsiteBuilderSectionEntityViewBoundingBox {
    let position: IWebsiteBuilderNodePosition = null;
    let dimensions: IWebsiteBuilderNodeDimensions = null;

    if (isWebsiteBuilderSectionEntityInSectionContext(elementContext)) {
      position = elementContext.activeScreenLayoutData.position;
      dimensions = elementContext.activeScreenLayoutData.dimensions;
    } else {
      position = elementContext.activeLayoutData.position;
      dimensions = elementContext.activeLayoutData.dimensions;
    }

    if (elementFilled) {
      const parentRef = activePageDomNodeRefMap.current.get(elementContext.parentNode.id);
      const parentBoundingRect = parentRef?.getBoundingClientRect();

      if (parentBoundingRect != null) {
        dimensions = {
          width: parentBoundingRect.width,
          height: parentBoundingRect.height,
        };
      }
    } else if (elementResizing) {
      position = viewResizePosition;
      dimensions = viewResizeDimensions;
    } else if (elementMoving) {
      position = viewMovePosition;
      dimensions = viewMoveDimensions;
    } else if (elementDetachedFromTab) {
      position = websiteBuilderGetTabsetPosition(position);
    }

    return {
      x: position.x,
      y: position.y,
      width: dimensions.width,
      height: dimensions.height,
    };
  }

  const elementBoundingBox = getElementBoundingBox();

  function getElementViewPrecedence(): CSSProperties {
    let opacity: number = 1;
    let zIndex: number = elementContext.zIndex;

    if (elementMoving || elementResizing) {
      opacity = ELEMENT_VIEW_MOTION_OPACITY;
    }

    if (elementMoving || elementResizing || elementSettingsActive || elementStretchSettingsActive || elementBuilderActive || elementContextMenuActive) {
      zIndex = WEBSITE_BUILDER_Z_LAYERS.designSpace.sectionEntityOverride;
    }

    return {
      opacity,
      zIndex,
    };
  }

  function renderChild(): JSX.Element {
    if (isWebsiteBuilderButton(elementContext.node)) {
      return <WebsiteBuilderButtonView node={elementContext.node} />;
    } else if (isWebsiteBuilderDivider(elementContext.node)) {
      return <WebsiteBuilderDividerView node={elementContext.node} />;
    } else if (isWebsiteBuilderEmbed(elementContext.node)) {
      return <WebsiteBuilderEmbedView node={elementContext.node} />;
    } else if (isWebsiteBuilderHeading(elementContext.node)) {
      return (
        <WebsiteBuilderHeadingView
          node={elementContext.node}
          elementBuilderUIPortalId={elementBuilderUIPortalId}
          sectionId={elementSectionNode.id}
        />
      );
    } else if (isWebsiteBuilderIcon(elementContext.node)) {
      return <WebsiteBuilderIconView node={elementContext.node} />;
    } else if (isWebsiteBuilderImage(elementContext.node)) {
      return <WebsiteBuilderImageView node={elementContext.node} />;
    } else if (isWebsiteBuilderLink(elementContext.node)) {
      return <WebsiteBuilderLinkView node={elementContext.node} />;
    } else if (isWebsiteBuilderList(elementContext.node)) {
      return <WebsiteBuilderListView node={elementContext.node} />;
    } else if (isWebsiteBuilderLogo(elementContext.node)) {
      return <WebsiteBuilderLogoView node={elementContext.node} />;
    } else if (isWebsiteBuilderNavigationMenu(elementContext.node)) {
      return <WebsiteBuilderNavigationMenuView />;
    } else if (isWebsiteBuilderParagraph(elementContext.node)) {
      return (
        <WebsiteBuilderParagraphView
          node={elementContext.node}
          elementBuilderUIPortalId={elementBuilderUIPortalId}
          sectionId={elementSectionNode.id}
        />
      );
    } else if (isWebsiteBuilderVideo(elementContext.node)) {
      return <WebsiteBuilderVideoView node={elementContext.node} />;
    } else {
      return null;
    }
  }

  function renderHiddenPlaceholder() {
    if (!elementDetachedFromFlowParent) {
      return null;
    }

    let dimensions: CSSProperties = {
      width: elementContext.activeLayoutData.dimensions.width,
      height: elementContext.activeLayoutData.dimensions.height,
    };

    if (elementMoving) {
      dimensions = {
        width: viewMoveDimensions.width,
        height: viewMoveDimensions.height,
      };
    }

    return (
      <div
        style={{
          visibility: "hidden",
          ...dimensions,
        }}
      />
    );
  }

  return (
    <>
      <div
        ref={activePageDomNodeRefMapRegistration}
        className={classNames(designStyles.className, "editor-element", {
          "element-selected": elementSelected,
          "element-builder": elementBuilderActive,
        })}
        style={{
          ...designStyles.styleProperties,
          ...(elementDetachedFromFlowParent ? { position: "absolute" } : {}),
          ...(elementFilled ? {
            width: "100%",
            height: "100%",
          } : {
            left: elementBoundingBox.x,
            top: elementBoundingBox.y,
            width: elementBoundingBox.width,
            height: elementBoundingBox.height,
          }),
          ...getElementViewPrecedence(),
        }}
        onContextMenu={e => {
          e.stopPropagation();
          e.preventDefault();

          if (!elementBuilderActive) {
            handleViewOpenContextMenu({ x: e.clientX, y: e.clientY });
          }
        }}
        onMouseOver={() => setElementHovered(true)}
        onMouseOut={() => setElementHovered(false)}
        onDoubleClick={e => {
          e.stopPropagation();

          if (isWebsiteBuilderElementWithBuilder(elementContext.node)) {
            if (!elementBuilderActive) {
              dispatch(
                websiteBuilderUpdateSelectedNode({
                  selectedNodeUpdates: {
                    type: "element",
                    editingMode: {
                      type: "element-builder",
                    },
                  },
                }),
              );
            }
          } else {
            handleViewSettingsDoubleClick();
          }
        }}
        onPointerDown={e => {
          e.stopPropagation();

          if (elementBuilderActive) {
            return;
          }

          e.preventDefault();

          if (!elementSelected) {
            dispatch(
              websiteBuilderSelectNode({
                selectedNode: {
                  type: "element",
                  sectionId: elementSectionNode.id,
                  containerId: elementContainerNode != null ? elementContainerNode.id : null,
                  gridCellId: elementGridCellNode != null ? elementGridCellNode.id : null,
                  tabId: elementTabNode != null ? elementTabNode.id : null,
                  id: elementContext.node.id,
                  editingMode: {
                    type: "none",
                  },
                },
              }),
            );
          }

          if (e.button === 0) {
            if (elementContextMenuActive) {
              dispatch(
                websiteBuilderUpdateSelectedNode({
                  selectedNodeUpdates: {
                    type: "element",
                    editingMode: {
                      type: "none",
                    },
                  },
                }),
              );
            }

            handleViewMovePointerDown(e);
          }
        }}
      >
        {renderChild()}
        {!elementBuilderActive && (
          <>
            {viewMoveIndicator}
            {viewResizeIndicator}
            {viewSettings}
            {viewStretchSettings}
            {viewContextMenu}
          </>
        )}

        {elementFrameIsChild && (
          <SectionEntityFrame
            context={elementContext}
            title={elementTitle}
            highlight={elementHighlight}
            boundingBox={elementBoundingBox}
            selected={elementSelected}
            moving={elementMoving}
            viewResizeHandles={viewResizeHandles}
          />
        )}
      </div>

      {!elementFrameIsChild && (
        <SectionEntityFrame
          context={elementContext}
          title={elementTitle}
          highlight={elementHighlight}
          boundingBox={elementBoundingBox}
          selected={elementSelected}
          moving={elementMoving}
          viewResizeHandles={viewResizeHandles}
        />
      )}

      {renderHiddenPlaceholder()}

      <div
        id={elementBuilderUIPortalId}
        style={{
          position: "absolute",
          left: elementBoundingBox.x,
          top: elementBoundingBox.y,
        }}
      />
    </>
  );
}

interface IWebsiteBuilderElementViewContextProviderProps {
  node: TWebsiteBuilderElement;
  parentNode: TWebsiteBuilderNode;
  grandParentNode: TWebsiteBuilderNode;
  greatGrandParentNode: TWebsiteBuilderNode;
}

export function WebsiteBuilderElementViewContextProvider(props: IWebsiteBuilderElementViewContextProviderProps) {
  const { node, parentNode, grandParentNode, greatGrandParentNode } = props;

  const deviceView = useAppSelector(store => store.websiteBuilderStore.editor.deviceView);
  
  const { designSpaceBoundaries } = useWebsiteBuilderEditorContext();

  const elementZIndex = node.element.zIndex;

  const elementStretch: IWebsiteBuilderSectionEntityStretch = isWebsiteBuilderStretchElementData(node.element)
    ? node.element.stretch
    : null;

  const elementContext: TWebsiteBuilderElementViewContext = useMemo(() => {
    const layout = node.element.layout;
    const activeLayoutData = websiteBuilderGetSectionEntityActiveLayoutData(node, deviceView);

    if (node?.type === "element" && parentNode?.type === "section" && grandParentNode?.type === "root") {
      const activeScreenLayoutData: IWebsiteBuilderSectionEntityLayoutData = {
        position: websiteBuilderGetSectionScreenPosition(designSpaceBoundaries.leftX, activeLayoutData.position),
        dimensions: { ...activeLayoutData.dimensions },
      };
      
      if (elementStretch?.enabled) {
        activeScreenLayoutData.position.x = elementStretch.margin;

        activeScreenLayoutData.dimensions.width =
          designSpaceBoundaries.leftX + designSpaceBoundaries.rightX - elementStretch.margin * 2;
      }

      return {
        type: "element-in-section",
        node,
        parentNode,
        rootNode: grandParentNode,
        layout,
        activeLayoutData,
        zIndex: elementZIndex,
        activeScreenLayoutData,
        stretch: elementStretch,
      };
    } else if (node?.type === "element" && isWebsiteBuilderElementContainerParentNode(parentNode) && grandParentNode?.type === "section" && greatGrandParentNode?.type === "root") {
      return {
        type: "element-in-container",
        node,
        parentNode,
        sectionNode: grandParentNode,
        layout,
        activeLayoutData,
        zIndex: elementZIndex,
      };
    } else if (node?.type === "element" && parentNode?.type === "grid-cell" && isWebsiteBuilderGrid(grandParentNode) && greatGrandParentNode?.type === "section") {
      return {
        type: "element-in-grid-cell",
        node,
        parentNode,
        gridNode: grandParentNode,
        sectionNode: greatGrandParentNode,
        layout,
        activeLayoutData,
        zIndex: elementZIndex,
      };
    } else if (node?.type === "element" && parentNode?.type === "tab" && isWebsiteBuilderTabset(grandParentNode) && greatGrandParentNode?.type === "section") {
      return {
        type: "element-in-tab",
        node,
        parentNode,
        tabsetNode: grandParentNode,
        sectionNode: greatGrandParentNode,
        layout,
        activeLayoutData,
        zIndex: elementZIndex,
      };
    } else {
      return null;
    }
  }, [
    node,
    deviceView,
    parentNode,
    grandParentNode,
    greatGrandParentNode,
    designSpaceBoundaries.leftX,
    designSpaceBoundaries.rightX,
    elementStretch,
    elementZIndex,
  ]);
  
  if (elementContext == null) {
    return null;
  } else {
    return (
      <SectionEntityPreviewProvider>
        <WebsiteBuilderElementView elementContext={elementContext} />
      </SectionEntityPreviewProvider>
    );
  }
}